import './styles.scss';

import React, { useEffect, useRef, useState } from 'react';
import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { t } from 'utils/i18n';
import type { FrameStatus } from './steps/steps-api';

const DOT = 'cvat-workspace-frame-strip-dot';

const STATUS_TEXT: Record<FrameStatus, string> = {
    done: 'all steps done',
    partial: 'some steps done',
    running: 'processing',
    failed: 'a step failed',
    pending: 'not processed',
};

interface StripFrame {
    number: number;
    url: string | null;
    // false hides the delete button: a photo of another job cannot be deleted from here
    deletable?: boolean;
    // false pins the tile: only the frames of the job being edited can be dragged
    draggable?: boolean;
    // the image file name, shown under the tile
    name?: string;
}

interface Props {
    frames: StripFrame[];
    current: number;
    onSelect(frame: number): void;
    onVisible(frame: number): void;
    status?: Record<number, FrameStatus>;
    selected?: number[];
    onToggleSelect?(frame: number): void;
    noun?: string;
    statusText?: Record<FrameStatus, string>;
    onAdd?(): void;
    onDelete?(frame: number): void;
    // gets the numbers of the draggable tiles in the new order when Save order is pressed; the page is loaded
    // again after it, a rejection puts the tiles back
    onReorder?(numbers: number[]): Promise<void>;
}

// moves one draggable tile onto the place of another; the pinned tiles stay where they are
function moveTile(frames: StripFrame[], from: number, to: number): StripFrame[] {
    const movable = frames.filter((frame) => frame.draggable !== false);
    const fromIndex = movable.findIndex((frame) => frame.number === from);
    const toIndex = movable.findIndex((frame) => frame.number === to);
    if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return frames;
    const [moved] = movable.splice(fromIndex, 1);
    movable.splice(toIndex, 0, moved);
    let next = 0;
    return frames.map((frame) => (frame.draggable !== false ? movable[next++] : frame));
}

export default function FrameStrip({
    frames: framesProp, current, onSelect, onVisible, status, selected, onToggleSelect, noun = 'Drawing', statusText, onAdd, onDelete,
    onReorder,
}: Props): JSX.Element {
    // the dragged order is kept as numbers: tiles themselves (thumbnails) keep coming from the props
    const [moved, setMoved] = useState<number[] | null>(null);
    const dragged = useRef<number | null>(null);
    const dropped = useRef(false);
    // the order before the drag in progress: letting go outside of the tiles puts it back
    const before = useRef<number[] | null>(null);
    // after a saved order the page is loaded again: the buttons and the tiles stay locked until then
    const [saving, setSaving] = useState(false);
    const byNumber = new Map(framesProp.map((frame) => [frame.number, frame]));
    const frames = moved ? moved.map((n) => byNumber.get(n)).filter((f): f is StripFrame => !!f) : framesProp;
    const listRef = useRef<HTMLDivElement>(null);
    // Rebuilding the observer on every render would re-announce frames that are still loading.
    const loadedRef = useRef(new Set<number>());
    loadedRef.current = new Set(frames.filter((f) => f.url).map((f) => f.number));
    const onVisibleRef = useRef(onVisible);
    onVisibleRef.current = onVisible;
    const numbersKey = frames.map((f) => f.number).join(',');

    useEffect(() => {
        const list = listRef.current;
        if (!list) return () => {};
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                const frame = Number((entry.target as HTMLElement).dataset.frame);
                if (entry.isIntersecting && !loadedRef.current.has(frame)) onVisibleRef.current(frame);
            });
        }, { root: list, rootMargin: '0px 200px' });
        list.querySelectorAll('[data-frame]').forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, [numbersKey]);

    const propKey = framesProp.map((f) => f.number).join(',');
    useEffect(() => setMoved(null), [propKey]);

    const moveDragged = (target: number): void => {
        if (dragged.current === null) return;
        const next = moveTile(frames, dragged.current, target);
        // dragenter also comes from the children of a tile: no new order, no render
        if (next !== frames) setMoved(next.map((f) => f.number));
    };

    const draggable = (list: StripFrame[]): number[] => list
        .filter((frame) => frame.draggable !== false).map((frame) => frame.number);
    // the dragged tiles are in another order than the saved one
    const changed = draggable(frames).join() !== draggable(framesProp).join();

    const saveOrder = (): void => {
        if (!onReorder) return;
        setSaving(true);
        onReorder(draggable(frames)).catch(() => {
            setMoved(null);
            setSaving(false);
        });
    };

    return (
        <div className='cvat-workspace-frame-strip' ref={listRef}>
            {frames.map((frame, index) => (
                <div
                    key={frame.number}
                    className='cvat-workspace-frame-strip-cell'
                    // on the wrapper, not on the button: Firefox does not drag the content of a button
                    draggable={!!onReorder && !saving && frame.draggable !== false}
                    data-dragging={dragged.current === frame.number ? 'true' : undefined}
                    onDragStart={(event) => {
                        // Firefox does not start a drag without data
                        event.dataTransfer?.setData('text/plain', String(frame.number));
                        dragged.current = frame.number;
                        dropped.current = false;
                        before.current = moved;
                    }}
                    onDragEnd={() => {
                        if (!dropped.current) setMoved(before.current);
                        dragged.current = null;
                    }}
                    onDragOver={(event) => { if (dragged.current !== null) event.preventDefault(); }}
                    // the tiles move while dragging, so the order is already shown before the drop
                    onDragEnter={() => moveDragged(frame.number)}
                    onDrop={(event) => {
                        event.preventDefault();
                        moveDragged(frame.number);
                        dropped.current = true;
                        dragged.current = null;
                    }}
                >
                    <button
                        type='button'
                        data-frame={frame.number}
                        data-status={status?.[frame.number]}
                        data-selected={selected?.includes(frame.number) ? 'true' : undefined}
                        title={[
                            frame.name,
                            status?.[frame.number] && `${t(`${noun} {n}`, { n: index + 1 })}: ${
                                t((statusText ?? STATUS_TEXT)[status[frame.number]])}`,
                        ].filter(Boolean).join('\n') || undefined}
                        aria-label={t(`${noun} {n}`, { n: index + 1 })}
                        aria-current={frame.number === current}
                        className='cvat-workspace-frame-strip-item'
                        onClick={(event) => {
                            if ((event.ctrlKey || event.metaKey) && onToggleSelect) onToggleSelect(frame.number);
                            else onSelect(frame.number);
                        }}
                    >
                        {frame.url ? <img src={frame.url} alt='' draggable={false} /> : <span className='cvat-workspace-frame-strip-placeholder' />}
                        <span className='cvat-workspace-frame-strip-number'>{index + 1}</span>
                        {status?.[frame.number] && (
                            <span className={`${DOT} ${DOT}-${status[frame.number]}`} />
                        )}
                        {onDelete && frame.deletable !== false && (
                            // a span: a button inside the tile's button is invalid markup
                            <span
                                role='button'
                                tabIndex={0}
                                className='cvat-workspace-frame-strip-delete'
                                aria-label={t(`Delete ${noun.toLowerCase()} {n}`, { n: index + 1 })}
                                title={t(`Delete ${noun.toLowerCase()}`)}
                                onClick={(event) => { event.stopPropagation(); onDelete(frame.number); }}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        onDelete(frame.number);
                                    }
                                }}
                            >
                                <CloseOutlined />
                            </span>
                        )}
                    </button>
                    {frame.name && (
                        <span className='cvat-workspace-frame-strip-name' aria-hidden>{frame.name.split('/').pop()}</span>
                    )}
                </div>
            ))}
            {changed && onReorder && (
                <>
                    <button
                        type='button'
                        className='cvat-workspace-frame-strip-save-order'
                        disabled={saving}
                        onClick={saveOrder}
                    >
                        {t('Save order')}
                    </button>
                    <button
                        type='button'
                        className='cvat-workspace-frame-strip-cancel-order'
                        disabled={saving}
                        onClick={() => setMoved(null)}
                    >
                        {t('Cancel')}
                    </button>
                </>
            )}
            {onAdd && (
                <button
                    type='button'
                    className='cvat-workspace-frame-strip-add'
                    aria-label={t(`Add ${noun.toLowerCase()}s`)}
                    title={t(`Add ${noun.toLowerCase()}s`)}
                    onClick={onAdd}
                >
                    <PlusOutlined />
                </button>
            )}
        </div>
    );
}
